// First party and third party event tracking

import ahoy from 'ahoy.js'
import { trackEventViaGoogle, trackPageViaGoogle } from 'src/boot/tracking/destinations/google'
import { identifyUserViaLiveSession, trackEventViaLiveSession } from 'src/boot/tracking/destinations/livesession'
import { trackEventViaMeta, trackPageViaMeta } from 'src/boot/tracking/destinations/meta'
import { trackEventViaSegment, trackPageViaSegment } from 'src/boot/tracking/destinations/segment'
import { identifyUserViaSessionRewind, trackEventViaSessionRewind } from 'src/boot/tracking/destinations/session-rewind'
import { CUSTOMER_EVENTS, INDY_USER_EVENTS } from 'src/boot/tracking/events'
import { appMode, isAdmin, isConsumer, isKds, isKiosk, isPos, isRoam, isSignage, siteId } from 'src/config'
import Model from 'src/models/model'

export default async ({ app, router }) => {
  if (isSignage) return

  await Model.context.ensureLoaded()

  const headers = {}
  const setHeaders = function () {
    if (isConsumer) {
      headers['site-id'] = siteId
    } else if (isPos || isKiosk) {
      headers['site-id'] = Model.context?.currentRegisterClient?.registerStation?.siteId
    } else if (isKds) {
      headers['site-id'] = Model.context?.currentUser?.siteId
    } else if (isAdmin) {
      headers['site-id'] = Model.context?.currentUser?.siteId
    } else if (isRoam) {
      headers['site-id'] = Model.context?.currentUser?.siteId
    } else {
      throw new Error('Unhandled app mode')
    }
    headers['client-type'] = appMode
    headers['user-id'] = Model.context?.currentUser?.id

    identifyUserViaLiveSession(Model.context?.currentUser?.id, { siteId })
    identifyUserViaSessionRewind(Model.context?.currentUser?.id, { siteId })

    // eslint-disable-next-line no-undef
    if (typeof Rollbar !== 'undefined') {
      if (Model.context?.currentUser && Model.context.currentUser.isRemote) {
        // eslint-disable-next-line no-undef
        Rollbar.configure({
          payload: {
            person: {
              id: Model.context.currentUser.id,
              email: Model.context.currentUser.email,
              name: Model.context.currentUser.name
            }
          }
        })
      } else {
        // eslint-disable-next-line no-undef
        Rollbar.configure({
          payload: {
            person: null
          }
        })
      }
    }
  }
  setHeaders()

  ahoy.configure({
    urlPrefix: '',
    visitsUrl: '/ahoy/visits',
    eventsUrl: '/ahoy/events',
    page: null,
    platform: 'Web',
    useBeacon: true,
    startOnReady: true,
    trackVisits: true,
    cookies: true,
    cookieDomain: null,
    headers,
    visitParams: {},
    withCredentials: false,
    visitDuration: 4 * 60, // 4 hours
    visitorDuration: 2 * 365 * 24 * 60 // 2 years
  })

  if (process.env.DEV) {
    // ahoy.debug()
  }

  app.mixin({
    async mounted () {
      if (!this.$options.trackPage || this.pageTracked) return

      this.$trackPage(this)

      this.pageTracked = true // Prevent double tracking for routes that fire both mounted and activated
      await this.$nextTick()
      this.pageTracked = false
    },
    async activated () {
      if (!this.$options.trackPage || this.pageTracked) return

      this.$trackPage(this)

      this.pageTracked = true // Prevent double tracking for routes that fire both mounted and activated
      await this.$nextTick()
      this.pageTracked = false
    }
  })

  /**
   * Track an event in our own analytics as well as on other supported platforms.
   * Prefer event names formatted in human-readable, startcase, past-tense
   * *Verbed Noun* format. For example: `"Clicked The Example Button"`.
   */
  function trackEvent (name, data = {}) {
    // If we're on iOS, only track if the user has granted permissions
    if (window._capacitorNative && !window._isIosTrackingAllowed) {
      return
    }

    name = _.startCase(name)

    data = snakeCaseKeys(data)
    if (process.env.DEV) {
      console.log('[Track Event] ' + name)
      if (_.keys(data).length) {
        console.log(JSON.stringify(data, null, 2))
      }
    }

    // if (process.env.DEV) return

    // Track via our own analytics
    try {
      ahoy.track(name, data)
    } catch (e) {
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Failed to track event via Ahoy', e)
      }
    }

    // Attempt to track the event in any supported third-party analytics platforms
    // that are present on the page (installed by the client). Clients are responsible
    // for configuring their analytics platforms and preventing duplicate events.
    trackEventViaGoogle(name, data)
    trackEventViaSegment(name, data)
    trackEventViaMeta(name, data)
    trackEventViaLiveSession(name, data)
    trackEventViaSessionRewind(name, data)
  }

  async function trackPage (component, data = {}) {
    // If we're on iOS, only track if the user has granted permissions
    if (window.Capacitor && window.Capacitor.isNativePlatform() && !window._isIosTrackingAllowed) {
      return
    }

    // Include default data
    const clientConfig = Model.configuration || {}
    const currentUser = Model.context?.currentUser || {}
    data = {
      circuit_id: clientConfig.currentCircuit?.id,
      circuit_name: clientConfig.currentCircuit?.name,
      site_id: clientConfig.currentSite?.id,
      site_name: clientConfig.currentSite?.name,
      user_id: currentUser.id,
      ...data
    }

    if (component) {
      if (_.isFunction(component.$options.trackPage)) {
        const customPageData = await component.$options.trackPage.call(component)
        data = { ...customPageData, ...data }
      }

      data = { name: component.$options.name, ...data }
    }

    if (!data.name) {
      console.warn('[WARN] No page name provided in page track call')
      return
    }

    data.name = _.startCase(data.name)
    data = snakeCaseKeys(data)

    if (process.env.DEV) {
      console.log('[Track Page] ' + data.name)
      if (_.keys(data).length > 1) {
        console.log(JSON.stringify(data, null, 2))
      }
    }

    // if (process.env.DEV) return

    // Track via our own analytics
    ahoy.configure({ page: data.name })
    try {
      ahoy.track('Page View', data)
    } catch (e) {
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Failed to track page via Ahoy', e)
      }
    }

    // Attempt to track the event in any supported third-party analytics platforms
    // that are present on the page (installed by the client). Clients are responsible
    // for configuring their analytics platforms and preventing duplicate events.
    trackPageViaGoogle(router?.currentRoute?.path, data)
    trackPageViaMeta(data)
    trackPageViaSegment(data.name, data)

    // Also send as an event that is more human readable
    const clonedData = _.clone(data)
    delete clonedData.name
    trackEvent(CUSTOMER_EVENTS.VIEWED_PAGE(data.name), clonedData)
  }

  app.config.globalProperties.$trackEvent = trackEvent
  app.config.globalProperties.$trackPage = trackPage
  app.config.globalProperties.$handleAuthChangeForTracking = setHeaders
  app.config.globalProperties.$indyUserEvents = INDY_USER_EVENTS
  app.config.globalProperties.$customerEvents = CUSTOMER_EVENTS
}

function snakeCaseKeys (data) {
  return _.transform(data, function (resultObject, value, key) {
    const newKey = _.snakeCase(_.trim(key))
    resultObject[newKey] = value
  })
}
